// Story 5.10 — Offer-management UI for /account/marketplace/listings.
//
//   * IncomingOffersPanel — seller-facing. Lists the active offers on one of
//     the seller's listings with Accept / Decline. Accepting executes the
//     sale at the offer price and auto-declines the rest.
//   * MyOffersSection — buyer-facing. The caller's own offers across both
//     item kinds, with Withdraw on active ones (the only way to free held
//     funds, since offers never auto-expire).

import { Button } from "@greenhold/components";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { formatCents } from "./feeMath";
import {
  useAcceptOffer,
  useListingOffers,
  useMyOffers,
  useRejectOffer,
  useWithdrawOffer,
  type OfferError,
  type OfferKind,
} from "./useMarketplaceOffers";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function OfferStatusBadge({ status }: { status: string }) {
  return (
    <span
      data-testid={`offer-status-${status}`}
      className="inline-flex items-center rounded border border-brand-paper-300 bg-brand-paper-200 px-2 py-0.5 font-mono text-[11px] uppercase tracking-wider text-brand-forest-900/75"
    >
      {status}
    </span>
  );
}

// =========================================================================
// Seller: incoming offers on one listing
// =========================================================================

export function IncomingOffersPanel({
  kind,
  listingId,
}: {
  kind: OfferKind;
  listingId: string;
}) {
  const queryClient = useQueryClient();
  const offers = useListingOffers(kind, listingId, true);
  const accept = useAcceptOffer(kind);
  const reject = useRejectOffer(kind);

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ["listing-offers", kind, listingId] });
    queryClient.invalidateQueries({ queryKey: ["my-listings"] });
    queryClient.invalidateQueries({ queryKey: ["my-offers"] });
    queryClient.invalidateQueries({ queryKey: ["wallet-balance"] });
    queryClient.invalidateQueries({ queryKey: ["marketplace-share-listings"] });
    queryClient.invalidateQueries({
      queryKey: ["marketplace-sealed-dossier-listings"],
    });
  }

  async function handleAccept(offerId: string) {
    try {
      await accept.mutateAsync({ offer_id: offerId });
      toast.success("Offer accepted — the sale is complete.");
      invalidate();
    } catch (err) {
      toast.error((err as OfferError)?.message ?? "Could not accept the offer.");
    }
  }

  async function handleReject(offerId: string, block: boolean) {
    try {
      await reject.mutateAsync({ offer_id: offerId, block });
      toast.success(
        block
          ? "Offer declined — this bidder can't re-offer on this listing."
          : "Offer declined — the buyer's hold was released.",
      );
      invalidate();
    } catch (err) {
      toast.error((err as OfferError)?.message ?? "Could not decline the offer.");
    }
  }

  if (offers.isLoading) {
    return (
      <p className="px-3 pb-3 font-mono text-[11px] uppercase tracking-wider text-brand-forest-900/50">
        Loading offers…
      </p>
    );
  }
  if (offers.isError) return null;

  const list = offers.data ?? [];
  const pending = accept.isPending || reject.isPending;

  return (
    <div className="rounded-md border border-brand-gold-500/30 bg-brand-paper-100 p-3">
      <div className="mb-2 flex items-center gap-2">
        <span className="font-mono text-[11px] font-medium uppercase tracking-wider text-brand-forest-700">
          Offers{list.length > 0 ? ` · ${list.length}` : ""}
        </span>
        <span className="h-px flex-1 bg-brand-gold-500/30" aria-hidden="true" />
      </div>
      {list.length === 0 ? (
        <p className="font-body text-xs text-brand-ink-700">
          No offers yet. Buyers who bid below your asking price show up here.
        </p>
      ) : (
        <ul className="space-y-2">
          {list.map((o) => (
            <li
              key={o.offer_id}
              data-testid={`incoming-offer-${o.offer_id}`}
              className="flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <span className="font-mono text-sm font-semibold tabular-nums text-brand-ink-900">
                  {formatCents(o.amount_cents)}
                </span>
                <span className="ml-2 font-body text-xs text-brand-ink-700">
                  {formatDate(o.created_at)}
                </span>
              </div>
              <div className="flex shrink-0 gap-2">
                <Button
                  size="sm"
                  onClick={() => handleAccept(o.offer_id)}
                  disabled={pending}
                  data-testid={`accept-offer-${o.offer_id}`}
                >
                  Accept
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleReject(o.offer_id, false)}
                  disabled={pending}
                  data-testid={`reject-offer-${o.offer_id}`}
                >
                  Decline
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleReject(o.offer_id, true)}
                  disabled={pending}
                  data-testid={`block-offer-${o.offer_id}`}
                  className="text-state-error-fill hover:text-state-error-fill"
                  aria-label="Decline and block this bidder from re-offering on this listing"
                  title="Decline and block this bidder from re-offering on this listing"
                >
                  Block
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// =========================================================================
// Buyer: my own offers
// =========================================================================

export function MyOffersSection() {
  const queryClient = useQueryClient();
  const shareOffers = useMyOffers("share");
  const sealedOffers = useMyOffers("sealed_dossier");
  const withdrawShare = useWithdrawOffer("share");
  const withdrawSealed = useWithdrawOffer("sealed_dossier");

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ["my-offers"] });
    queryClient.invalidateQueries({ queryKey: ["wallet-balance"] });
  }

  async function handleWithdraw(kind: OfferKind, offerId: string) {
    const mutation = kind === "share" ? withdrawShare : withdrawSealed;
    try {
      await mutation.mutateAsync({ offer_id: offerId });
      toast.success("Offer withdrawn — your held funds were released.");
      invalidate();
    } catch (err) {
      toast.error((err as OfferError)?.message ?? "Could not withdraw the offer.");
    }
  }

  const isLoading = shareOffers.isLoading || sealedOffers.isLoading;
  const rows = [
    ...(shareOffers.data ?? []).map((o) => ({ kind: "share" as OfferKind, o })),
    ...(sealedOffers.data ?? []).map((o) => ({
      kind: "sealed_dossier" as OfferKind,
      o,
    })),
  ].sort((a, b) => b.o.created_at.localeCompare(a.o.created_at));

  const withdrawing = withdrawShare.isPending || withdrawSealed.isPending;

  return (
    <section data-testid="my-offers-section">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-heading text-lg font-semibold">My offers</h2>
      </div>
      {isLoading && <p className="text-sm opacity-60">Loading…</p>}
      {!isLoading && rows.length === 0 && (
        <p
          data-testid="my-offers-empty"
          className="rounded-lg border border-brand-paper-300 bg-brand-paper-50 p-4 text-sm text-brand-ink-700"
        >
          You haven&apos;t made any offers yet. Find a listing that accepts
          offers and bid below its asking price.
        </p>
      )}
      {rows.length > 0 && (
        <ul className="rounded-lg border border-brand-paper-300 bg-brand-paper-50">
          {rows.map(({ kind, o }) => (
            <li
              key={o.offer_id}
              data-testid={`my-offer-row-${o.offer_id}`}
              className="flex items-center justify-between gap-3 border-b border-brand-paper-200 p-3 last:border-b-0"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">
                  {kind === "share" ? "Certificate" : "Sealed dossier"} offer
                </p>
                <p className="text-xs text-brand-ink-700">
                  You offered{" "}
                  <span className="font-mono font-semibold">
                    {formatCents(o.amount_cents)}
                  </span>{" "}
                  · asking {formatCents(o.listing_price_cents)}
                </p>
                <p className="text-xs text-brand-ink-700">
                  <span className="opacity-60">Placed</span>{" "}
                  {formatDate(o.created_at)}
                </p>
              </div>
              <OfferStatusBadge status={o.status} />
              <div className="flex w-32 justify-end">
                {o.status === "active" && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleWithdraw(kind, o.offer_id)}
                    disabled={withdrawing}
                    data-testid={`withdraw-offer-${o.offer_id}`}
                  >
                    Withdraw
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
